import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { essentialsService, EssentialsDashboard as DashboardData } from '../../services/essentialsService';
import { crmService } from '../../services/crmService';
import { ListPageHeader } from '../../components/crm/ListPageHeader';
import { leadSourceBadgeClass } from '../../utils/statusStyles';
import ContactDetailPanel from '../../components/essentials/ContactDetailPanel';
import FirstRunTour from '../../components/essentials/FirstRunTour';
import LiteUpgradeCta from '../../components/essentials/LiteUpgradeCta';

function relTime(iso?: string | null): string {
  if (!iso) return '—';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 60) return `${Math.max(min, 0)}m ago`;
  if (min < 1440) return `${Math.round(min / 60)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function dueLabel(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const KpiCard: React.FC<{ label: string; value: number; onClick: () => void; accent?: boolean }> = ({
  label,
  value,
  onClick,
  accent,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-2xl border p-5 text-left shadow-sm transition-colors hover:border-primary/50 ${
      accent ? 'border-warning-fg/40 bg-warning-fg/5' : 'border-border bg-surface'
    }`}
  >
    <div className="text-3xl font-bold tabular-nums text-fg">{value}</div>
    <div className="mt-1 text-sm text-fg-muted">{label}</div>
  </button>
);

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">{title}</h2>
    {children}
  </div>
);

const ConnectionStatus: React.FC = () => (
  <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-success-fg/30 bg-success-fg/10 px-3 py-1 text-xs font-medium text-success-fg">
    <span className="h-2 w-2 rounded-full bg-success-fg" aria-hidden />
    Website form &amp; chatbot connected
  </div>
);

const EssentialsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await essentialsService.getDashboard());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const completeFollowUp = async (id: string) => {
    await crmService.markClientFollowedUp(id).catch(() => undefined);
    load();
  };

  if (loading && !data) {
    return <div className="py-12 text-center text-fg-muted">Loading…</div>;
  }

  const k = data?.kpis;
  const monthDelta = data ? data.leads_this_month.count - data.leads_this_month.previous : 0;

  return (
    <div className="space-y-6">
      <FirstRunTour />
      <ListPageHeader title="Dashboard" subtitle="Who contacted you, who needs follow-up, and who is becoming a customer." />

      {error ? <div className="alert-danger rounded-xl px-4 py-3">{error}</div> : null}

      {/* KPI cards — 4 across, 2x2 on mobile; each is a filter shortcut */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="New Leads (this week)" value={k?.new_leads_week ?? 0} onClick={() => navigate('/leads')} />
        <KpiCard label="Active Prospects" value={k?.active_prospects ?? 0} onClick={() => navigate('/prospects')} />
        <KpiCard label="Customers" value={k?.customers ?? 0} onClick={() => navigate('/clients')} />
        <KpiCard label="Follow-Ups Due" value={k?.follow_ups_due ?? 0} accent onClick={() => navigate('/leads?followup=due')} />
      </div>

      {data && data.leads_this_month.count + data.leads_this_month.previous > 0 ? (
        <p className="text-sm text-fg-muted">
          <span className="font-semibold text-fg">{data.leads_this_month.count}</span> leads this month
          {monthDelta !== 0 ? (
            <span className={monthDelta > 0 ? 'text-success-fg' : 'text-danger-fg'}>
              {' '}({monthDelta > 0 ? '+' : ''}{monthDelta} vs last month)
            </span>
          ) : null}
        </p>
      ) : null}

      {/* Recent Leads */}
      <SectionCard title="Recent Leads">
        {data && data.recent_leads.length > 0 ? (
          <ul className="divide-y divide-border">
            {data.recent_leads.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-fg">{r.full_name}</div>
                  <div className="truncate text-xs text-fg-muted">{r.company_name || '—'}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {r.source ? <span className={leadSourceBadgeClass(r.source)}>{r.source}</span> : null}
                  <span className="text-xs text-fg-muted">{relTime(r.created_at)}</span>
                  <span className="text-xs text-fg-muted">{r.assigned_to_name || 'Unassigned'}</span>
                  <button type="button" onClick={() => setOpenId(r.id)} className="text-sm font-medium text-primary hover:underline">
                    View
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-6 text-center">
            <p className="font-medium text-fg">No leads yet — your website form and chatbot are live and connected</p>
            <p className="mt-1 text-sm text-fg-muted">New leads will show up here the moment someone reaches out.</p>
            <ConnectionStatus />
          </div>
        )}
      </SectionCard>

      {/* Follow-Ups Due */}
      <SectionCard title="Follow-Ups Due">
        {data && data.follow_ups_due.length > 0 ? (
          <ul className="divide-y divide-border">
            {data.follow_ups_due.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-fg">{r.full_name}</div>
                  <div className={`text-xs ${r.overdue ? 'font-semibold text-danger-fg' : 'text-fg-muted'}`}>
                    {r.overdue ? 'Overdue · ' : ''}{dueLabel(r.next_follow_up_at)}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => completeFollowUp(r.id)} className="btn-primary rounded-lg px-3 py-1.5 text-sm">
                    Complete
                  </button>
                  <button type="button" onClick={() => setOpenId(r.id)} className="btn-secondary rounded-lg px-3 py-1.5 text-sm">
                    Reschedule
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-6 text-center">
            <p className="font-medium text-fg">You're all caught up</p>
            <p className="mt-1 text-sm text-fg-muted">Follow-ups you schedule will appear here.</p>
          </div>
        )}
      </SectionCard>

      {/* Recent Website Activity */}
      <SectionCard title="Recent Website Activity">
        {data && data.recent_website_activity.length > 0 ? (
          <ul className="divide-y divide-border">
            {data.recent_website_activity.map((a, i) => (
              <li key={`${a.kind}-${a.session_id || a.contact_id || i}`} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-fg">{a.name}</div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.type === 'Chat' ? 'bg-primary/10 text-primary' : 'bg-warning-fg/10 text-warning-fg'}`}>
                    {a.type}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-xs text-fg-muted">{relTime(a.timestamp)}</span>
                  {a.kind === 'chat' && a.session_id ? (
                    <button type="button" onClick={() => navigate(`/chatbot/inbox/${a.session_id}`)} className="text-sm font-medium text-primary hover:underline">
                      View Conversation
                    </button>
                  ) : a.contact_id ? (
                    <button type="button" onClick={() => setOpenId(a.contact_id!)} className="text-sm font-medium text-primary hover:underline">
                      View
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-6 text-center">
            <p className="font-medium text-fg">No website activity yet</p>
            <p className="mt-1 text-sm text-fg-muted">Chats and form submissions from your site will appear here.</p>
            <ConnectionStatus />
          </div>
        )}
      </SectionCard>

      <LiteUpgradeCta />

      {openId ? (
        <ContactDetailPanel contactId={openId} onClose={() => setOpenId(null)} onChanged={() => load()} />
      ) : null}
    </div>
  );
};

export default EssentialsDashboard;
